import { type OperationVariables } from '@apollo/react-common';
import { type GraphQLTaggedNode } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { ApolloErrorInterop } from '@confluence/apollo-relay-interop-utils/entry-points/ApolloErrorInterop.ts';

import { executeRelayQuery } from './executeRelayQuery.ts';
import { isRelayEnabled } from './isRelayEnabled.ts';
import { toDocumentNode } from './toDocumentNode.ts';
import { type QueryInteropFetchPolicy } from './mapFetchPolicyForQuery.ts';
import { mapFetchPolicyForApollo } from './mapFetchPolicyForApollo.ts';
import {
	retainRelayQueryPreload,
	type RelayQueryPreloadRetentionLifecycle,
} from './relayQueryPreloadRetention.ts';
import {
	syncQueryCacheBridge,
	type QueryCacheBridgeSource,
} from './cacheBridge/queryCacheBridgeSync.ts';
import { getRelayOperationName } from './relayOperation.ts';

export type QueryInteropSupportedResult<TData> = {
	data: TData;
	error?: ApolloErrorInterop;
};

export type QueryInteropSupportedArgs<
	_TData,
	TVariables extends Record<string, any> = OperationVariables,
> = {
	query: GraphQLTaggedNode;
	variables?: TVariables;
	errorPolicy?: 'none' | 'ignore' | 'all';
	fetchPolicy?: QueryInteropFetchPolicy;
	context?: Record<string, any>;
	/**
	 * Ensures that the interop in Apollo build variant uses the AGG client
	 */
	interopApolloAggClient?: boolean;
	/**
	 * Overrides the Apollo-vs-Relay decision for this call instead of asking `isRelayEnabled`.
	 *
	 * Single purpose: SSR fires a small number of queries before the Statsig client is initialised,
	 * where `isRelayEnabled` cannot read the rollout configuration and silently answers "Apollo"
	 * regardless of how it is configured. If the same query is later read after initialisation, the
	 * two disagree and it is written to one cache and read from the other. Bifrost resolves the
	 * routing for those queries from the native edge SDK and passes the answer here.
	 *
	 * Do NOT use this to opt a package into Relay — that is what
	 * `confluence_relay_enabled_packages` is for. See `ssrForcedRelay.ts`.
	 */
	forceRelay?: boolean | null;
	/**
	 * Controls how long Relay retains a preloaded operation. Ignored when the call routes to Apollo.
	 * `environment` retains it for the Relay environment's lifetime; `hook-unmount` releases it when
	 * the matching useQueryInterop consumer unmounts.
	 */
	relayQueryPreloadRetention?: RelayQueryPreloadRetentionLifecycle;
	/** @internal Injected by RelayInteropBabelPlugin — do not set manually */
	__callerPackage__?: string;
};

export function queryInterop<TData, TVariables extends Record<string, any> = OperationVariables>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	const operationName = getRelayOperationName(params.query);
	// `??` short-circuits, so `isRelayEnabled` is not consulted (and fires no experiment exposure)
	// when the caller has already resolved the routing.
	const routeToRelay =
		params.forceRelay ??
		isRelayEnabled(params.__callerPackage__, {
			operationName,
			phase: 'preload',
		});
	const source: QueryCacheBridgeSource = routeToRelay ? 'relay' : 'apollo';

	return queryInteropWithBridge<TData, TVariables>(params, source);
}

async function queryInteropWithBridge<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
	source: QueryCacheBridgeSource,
): Promise<QueryInteropSupportedResult<TData>> {
	const result =
		source === 'relay'
			? await queryInteropRelay<TData, TVariables>(params)
			: await queryInteropApollo<TData, TVariables>(params);

	// Keep the original query result contract intact, then mirror successful
	// results into the secondary cache as best-effort bridge work.
	syncQueryCacheBridge({
		source,
		error: result.error,
		query: params.query,
		variables: params.variables,
		data: result.data,
		fetchPolicy: params.fetchPolicy,
		useAggClient: params.interopApolloAggClient,
	});

	return result;
}

async function queryInteropRelay<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>(
	params: QueryInteropSupportedArgs<TData, TVariables>,
): Promise<QueryInteropSupportedResult<TData>> {
	const relayEnv = window.GLOBAL_RELAY_ENV;

	if (!relayEnv) {
		// eslint-disable-next-line no-console
		console.warn(
			`[Relay migration] Missing GLOBAL_RELAY_ENV for queryInterop; falling back to Apollo for package ${params.__callerPackage__}.`,
		);
		return queryInteropApollo<TData, TVariables>(params);
	}

	const releasePreloadRetention =
		params.relayQueryPreloadRetention && params.fetchPolicy !== 'no-store'
			? retainRelayQueryPreload(
					relayEnv,
					params.query,
					params.variables ?? ({} as TVariables),
					params.relayQueryPreloadRetention,
				)
			: null;

	// `executeRelayQuery` owns the fetch-policy mapping and the `noStore` derivation. That matters
	// most on this path: it runs during SSR, where whatever normalizes into the store is serialized
	// into `__RELAY_STORE__` and hydrated on the client before its first fetch, so a missed `noStore`
	// re-poisons the store server-side and makes the client-side bypass pointless.
	try {
		return await executeRelayQuery<TData, TVariables>({
			environment: relayEnv,
			query: params.query,
			variables: params.variables ?? ({} as TVariables),
			fetchPolicy: params.fetchPolicy,
			errorPolicy: params.errorPolicy,
			context: params.context,
		});
	} catch (error) {
		releasePreloadRetention?.();
		throw error;
	}
}

async function queryInteropApollo<
	TData,
	TVariables extends Record<string, any> = OperationVariables,
>({
	query,
	fetchPolicy,
	...rest
}: QueryInteropSupportedArgs<TData, TVariables>): Promise<QueryInteropSupportedResult<TData>> {
	const client = rest.interopApolloAggClient
		? (window['GLOBAL_APOLLO_AGG_CLIENT'] ?? (window as any)['MOCK_GATEWAY_APOLLO_CLIENT'])!
		: window['GLOBAL_APOLLO_CLIENT']!;

	// `client.query` only accepts the narrower `FetchPolicy` (no `cache-and-network`/`standby`).
	// Map the watch-only values to the closest one-shot equivalent. `mapFetchPolicyForApollo` first,
	// so the interop-only `'no-store'` becomes `'no-cache'` rather than falling through to Apollo's
	// default cache behaviour.
	const apolloFetchPolicy = mapFetchPolicyForApollo(fetchPolicy);
	const oneShotFetchPolicy =
		apolloFetchPolicy === 'cache-and-network' || apolloFetchPolicy === 'standby'
			? 'cache-first'
			: apolloFetchPolicy;

	const result = await client.query({
		...rest,
		fetchPolicy: oneShotFetchPolicy,
		query: toDocumentNode(query),
	});

	return {
		data: result.data,
		error: result.errors?.length
			? new ApolloErrorInterop({ graphQLErrors: result.errors })
			: undefined,
	};
}

export function convertToApolloError(error: Error): ApolloErrorInterop {
	// TODO: Implement proper error conversion
	return error as ApolloErrorInterop;
}
